import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { usePost } from '../API';
import { logAdminPanelError } from '../utils/adminPanel.utils';

export type Invoice = {
  id: string;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  total_amount: number;
  status: 'Unpaid' | 'Paid' | 'Partially Paid' | 'Partially Credited' | 'Credited' | 'Overdue' | 'Pending';
};

export type InvoiceFilterParams = {
  issueStartDate?: string;
  issueEndDate?: string;
  dueStartDate?: string;
  dueEndDate?: string;
  statuses?: Invoice['status'][];
};

type InvoicesContextType = {
  allInvoices: Invoice[];
  filteredInvoices: Invoice[];
  isLoading: boolean;
  error: string | null;
  filters: InvoiceFilterParams;
  selectedRows: Set<string>;
  setFilters: (filters: InvoiceFilterParams) => void;
  toggleRowSelection: (id: string) => void;
  toggleAllRows: () => void;
  fetchInvoices: () => Promise<void>;
};

const InvoicesContext = createContext<InvoicesContextType | undefined>(undefined);

export const useInvoicesContext = () => {
  const context = useContext(InvoicesContext);
  if (!context) {
    throw new Error('useInvoicesContext must be used within an InvoicesProvider');
  }
  return context;
};

type InvoicesProviderProps = {
  children: ReactNode;
};

export const InvoicesProvider: React.FC<InvoicesProviderProps> = ({ children }) => {
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<InvoiceFilterParams>({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  
  const getInvoices = usePost('/api/admin_panel/invoices');

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getInvoices({});
      if (result.data?.invoices) {
        const transformed = result.data.invoices.map((inv: any) => ({
          id: inv.rillet_invoice_id,
          invoice_number: inv.invoice_number,
          invoice_date: inv.invoice_date || null,
          due_date: inv.due_date || null,
          total_amount: inv.total_amount ? parseFloat(inv.total_amount) / 100 : 0,
          status: inv.status as Invoice['status'],
        }));
        setAllInvoices(transformed);
      } else if (result.error) {
        logAdminPanelError(result.error, result.message);
        setError(result.message || 'Failed to load invoices');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load invoices';
      setError(errorMessage);
      console.error('Error loading invoices:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getInvoices]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filteredInvoices = useMemo(() => {
    let filtered = allInvoices;

    // Filter: only show invoices that are in the past or within 30 days from today.
    // Rillet generates invoices years into the future... we really don't need to display all of those. 
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    filtered = filtered.filter(invoice => {
      if (!invoice.invoice_date) return false;
      const invoiceDate = new Date(invoice.invoice_date);
      invoiceDate.setHours(0, 0, 0, 0);
      return invoiceDate <= thirtyDaysFromNow;
    });

    if (filters.issueStartDate) {
      filtered = filtered.filter(invoice => 
        invoice.invoice_date && new Date(invoice.invoice_date) >= new Date(filters.issueStartDate!)
      );
    }
    if (filters.issueEndDate) {
      filtered = filtered.filter(invoice => 
        invoice.invoice_date && new Date(invoice.invoice_date) <= new Date(filters.issueEndDate!)
      );
    }

    if (filters.dueStartDate) {
      filtered = filtered.filter(invoice => 
        invoice.due_date && new Date(invoice.due_date) >= new Date(filters.dueStartDate!)
      );
    }
    if (filters.dueEndDate) {
      filtered = filtered.filter(invoice => 
        invoice.due_date && new Date(invoice.due_date) <= new Date(filters.dueEndDate!)
      );
    }

    if (filters.statuses && filters.statuses.length > 0) {
      filtered = filtered.filter(invoice => 
        filters.statuses!.includes(invoice.status)
      );
    }

    return filtered;
  }, [allInvoices, filters]);

  const handleFiltersChange = useCallback((newFilters: InvoiceFilterParams) => {
    setFilters(newFilters);
    setSelectedRows(new Set());
  }, []);

  const toggleRowSelection = useCallback((id: string) => {
    setSelectedRows(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  }, []);

  const toggleAllRows = useCallback(() => {
    setSelectedRows(prev => {
      if (prev.size === filteredInvoices.length) {
        return new Set();
      } else {
        return new Set(filteredInvoices.map(inv => inv.id));
      }
    });
  }, [filteredInvoices]);

  const value: InvoicesContextType = {
    allInvoices,
    filteredInvoices,
    isLoading,
    error,
    filters,
    selectedRows,
    setFilters: handleFiltersChange,
    toggleRowSelection,
    toggleAllRows,
    fetchInvoices,
  };

  return (
    <InvoicesContext.Provider value={value}>
      {children}
    </InvoicesContext.Provider>
  );
};
